import React from "react";
import { DollarSign, TrendingUp, Clock, Wallet } from "lucide-react";

interface EarningsCardProps {
  earnings: {
    totalEstimated: number;
    fromCompleted: number;
    fromActive: number;
    availableBalance: number;
    avgHourlyRate: number;
    totalHoursWorked: number;
  };
  loading?: boolean;
}

const EarningsCard: React.FC<EarningsCardProps> = ({
  earnings,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-24"></div>
          <div className="h-10 bg-gray-200 rounded w-32"></div>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 text-green-600" />
        <h3 className="font-semibold text-gray-900">Earnings Overview</h3>
      </div>

      <div className="mb-4">
        <p className="text-3xl font-bold text-gray-900">
          ${earnings.totalEstimated.toLocaleString()}
        </p>
        <p className="text-sm text-gray-500">Total Estimated Earnings</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center gap-1 text-green-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">Completed</span>
          </div>
          <p className="text-lg font-semibold text-gray-900">
            ${earnings.fromCompleted.toLocaleString()}
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-1 text-blue-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">In Progress</span>
          </div>
          <p className="text-lg font-semibold text-gray-900">
            ${earnings.fromActive.toLocaleString()}
          </p>
        </div>

        <div className="bg-emerald-50 rounded-lg p-3">
          <div className="flex items-center gap-1 text-emerald-600 mb-1">
            <Wallet className="w-4 h-4" />
            <span className="text-xs font-medium">Available</span>
          </div>
          <p className="text-lg font-semibold text-gray-900">
            ${earnings.availableBalance.toLocaleString()}
          </p>
        </div>

        <div className="bg-purple-50 rounded-lg p-3">
          <div className="flex items-center gap-1 text-purple-600 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-medium">Avg Rate</span>
          </div>
          <p className="text-lg font-semibold text-gray-900">
            ${earnings.avgHourlyRate}/hr
          </p>
        </div>
      </div>
    </div>
  );
};

export default EarningsCard;
